import {Box, Button, useBreakpointValue, useDisclose} from 'native-base';
import {Location, LocationType, locationId} from '@shared/models/location';
import React, {useState} from 'react';
import ButtonBottomWrapper from '@ui/components/buttons/ButtonBottomWrapper';
import {CheckoutCreateError} from '@shared/models/checkout';
import {Competition} from '@shared/models/competition';
import LocationModal from './LocationModal';
import LoginModal from '@ui/features/auth/login/LoginModal';
import {ParticipantType} from '@shared/models/participant';
import SelectClubModal from '@ui/features/auth/selectClub/SelectClubModal';
import {TeamSelectModal} from '@ui/features/team/select/TeamSelectScreen';
import {delay} from 'lodash';
import {formatPrice} from '@ui/helpers/price';
import {getScreen} from '@ui/provider/navigation/screens';
import {isCompetitionFree} from '@shared/helpers/competition-fn';
import {track} from '@ui/helpers/analytics/analytics';
import useCheckout from '@ui/features/checkout/useCheckout';
import useCrouton from '@ui/hooks/useCrouton';
import {useRouter} from 'solito/router';
import {useTranslation} from 'react-i18next';
import {useUpcomingFixturesForCompetition} from '@ui/features/fixture/hooks/fixture-hooks';
import {useUser} from '@ui/provider/user/UserProvider';

const Wrapper = (props: {children: JSX.Element}) => {
    const isLarge = useBreakpointValue({
        base: false,
        lg: true,
    });

    if(isLarge) {
        return props.children;
    }
    return (<ButtonBottomWrapper>{props.children}</ButtonBottomWrapper>);
};

interface Props{
    competition: Competition;
    isLoading: boolean;
    hasRegistration?: boolean;
    text?: string;
}

const CompetitionDetailJoinButton = (props: Props) => {
    const {t} = useTranslation();
    const {competition, hasRegistration = false, isLoading, text} = props;
    const {isLoggedIn, user} = useUser();
    const {push} = useRouter();
    const {onCreateCheckoutSession, isCreateCheckoutSessionLoading} = useCheckout(competition.id);
    const [fixtures] = useUpcomingFixturesForCompetition(user?.uid, competition.id);
    const [location, setLocation] = useState<Location>();

    const crouton = useCrouton();

    const {onOpen: onOpenTeamSelect, isOpen: isTeamSelectOpen, onClose: onCloseTeamSelect} = useDisclose();
    const {onOpen: onOpenLogin, isOpen: isLoginOpen, onClose: onCloseLogin} = useDisclose();
    const {onOpen: onOpenSelectClub, isOpen: isSelectClubOpen, onClose: onCloseSelectClub} = useDisclose();
    const {onOpen: onOpenLocationModal, isOpen: isLocationModalOpen, onClose: onCloseLocationModal} = useDisclose();

    const multipleLocations = competition.registration.locationType !== LocationType.Establishment && competition.registration.locations.length > 1;
    const entryPrice = competition.registration.entryPrice;

    const hasPlayableFixtures = fixtures && fixtures.size > 0;
    const buttonText =
    text ??
    hasRegistration ?
        t('competition.action.playAgainPrice', {price: isCompetitionFree(competition) ? t('price.free') : formatPrice(entryPrice)}) :
        t('competition.action.joinPrice', {price: isCompetitionFree(competition) ? t('price.free') : formatPrice(entryPrice)});

    if(hasPlayableFixtures) {
        return null;
    }

    const handleCreateCheckoutSession = async(teamId?: string) => {
        if(multipleLocations && !location) {
            onOpenLocationModal();
            return;
        }
        const response = await onCreateCheckoutSession(teamId, location ?? competition.registration.locations[0]);

        if(!response) {
            return;
        }
        const {checkoutSessionId, error, hasJoined, fixtureId} = response;

        if(checkoutSessionId) {
            track('Checkout Started', {
                competition_id: competition.id,
                ...teamId && {team_id: teamId},
                ...location && {location_id: locationId(location)},
            });
            push({
                pathname: getScreen('checkout'),
                query: {
                    id: checkoutSessionId,
                },
            });
        }

        if(hasJoined) {
            crouton.show({
                title: t('actions.success'),
                message: t('competition.action.joinedSuccessfully'),
            });
        }

        if(fixtureId) {
            push({
                pathname: getScreen('fixture'),
                query: {
                    id: fixtureId,
                },
            });
        }

        if(error) {
            switch (error) {
                case CheckoutCreateError.MissingLocation: return onOpenSelectClub();
                case CheckoutCreateError.TeamMissing: return onOpenTeamSelect();
                default : return ;
            }
        }
    };

    const onPress = async() => {
        onCloseTeamSelect();
        onCloseLogin();
        if(!isLoggedIn) {
            onOpenLogin();
            return;
        }

        if(multipleLocations) {
            onOpenLocationModal();
            return;
        }

        if(competition.participants.type === ParticipantType.Team) {
            onOpenTeamSelect();
            return;
        }

        await handleCreateCheckoutSession();
    };

    const onTeamSelect = async(teamId: string) => {
        onCloseTeamSelect();
        delay(() => handleCreateCheckoutSession(teamId), 500);
    };

    const onFinishLogin = async() => {
        onCloseLogin();
        // I'm very sorry for this hack, but I'm too dumb to fix it
        delay(onPress, 500);
    };

    const onSetLocation = async(location: Location) => {
        setLocation(location);
        onCloseLocationModal();

        if(competition.participants.type === ParticipantType.Team) {
            onOpenTeamSelect();
            return;
        }

        await handleCreateCheckoutSession();
    };

    return (
        <Wrapper>
            <Box h='12'>
                <Button
                    variant="primary"
                    onPress={onPress}
                    isLoading={isCreateCheckoutSessionLoading || isLoading}
                    flex={1}
                >
                    {buttonText}
                </Button>

                <TeamSelectModal onClose={onCloseTeamSelect} isOpen={isTeamSelectOpen} onTeamSelect={onTeamSelect}/>

                <LoginModal onClose={onCloseLogin} isOpen={isLoginOpen} onFinish={onFinishLogin}/>

                <SelectClubModal onClose={onCloseSelectClub} isOpen={isSelectClubOpen} onFinish={onPress}/>

                <LocationModal onClose={onCloseLocationModal} isOpen={isLocationModalOpen} setLocation={onSetLocation} locations={competition.registration.locations}/>
            </Box>
        </Wrapper>
    );
};

export default CompetitionDetailJoinButton;
